/**
 * What visitors do on the homepage landing (#1482).
 *
 * The buy/rent/lease landing is Andys' mobile homepage. `sg_landing_ready`
 * (landingTiming.js) says it showed; the `sg_landing_*` events below say what
 * the visitor did next: which tab they went to and which way they left. Every
 * event that leaves the landing carries `destination`, so one GROUP BY gives
 * the path split:
 *   `catalog`, `rental`, `leasing`, `lease_form`, `chat`, `phone`, `bfc`,
 *   `menu:<item>`, `home` (the landing was closed — the host page underneath).
 *
 * The presentational landing reports each event with its own params and the
 * active tab (`onTrack`); `trackLandingEvent` adds what every landing event
 * shares and hands it to the analytics beacon:
 * - `view` — which landing (`buy-rent-lease`);
 * - `tab` — the tab active at the time;
 * - `msSinceReady` — ms since the landing showed: since `sg_landing_ready`
 *   where the homepage marker sent it, else since the landing mounted (the
 *   `widget-view` overlay, the `/andys` preview);
 * - the beacon's own `browsingSessionId` and `device_type`.
 *
 * No personal data: the only free text is the Buy search, trimmed and cut to
 * LANDING_QUERY_MAX characters.
 *
 * One landing per page (the homepage marker or the widget-view overlay), so its
 * view, tab and show time live here, at module level: the close event comes
 * from the overlay, which neither renders the tabs nor knows when the landing
 * showed.
 */
import { trackEvent } from './analyticsBeacon';

export const LANDING_QUERY_MAX = 100;

const now = () =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();

const landing = { view: null, tab: null, readyAt: null };

/**
 * The landing mounted. Its show time stands in for `sg_landing_ready` until
 * (and unless) that fires. Every mount restarts the clock: the preview route
 * can be left and opened again. The homepage marker's `sg_landing_ready` comes
 * after the mount (a frame later), so it still wins.
 *
 * @param {{view: string, tab: string}} opts - the view and the tab it opens on
 */
export function noteLandingShown({ view, tab }, at = now()) {
  landing.view = view;
  landing.tab = tab;
  landing.readyAt = at;
}

/** The landing is on screen (`sg_landing_ready`): `msSinceReady` counts from here. */
export function markLandingReady(at = now()) {
  landing.readyAt = at;
}

/** Car-search text as sent: whitespace collapsed, at most LANDING_QUERY_MAX characters. */
export const landingQuery = (value) =>
  String(value ?? '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, LANDING_QUERY_MAX);

/**
 * The stored params of a landing event: the common ones, then the event's own.
 * Pure — the state comes in `ctx`.
 *
 * @param {object} params - the event's own params; its `tab`, if any, wins
 * @param {{view?: string|null, tab?: string|null, readyAt?: number|null, at?: number}} [ctx]
 */
export function landingEventParams(params = {}, { view = null, tab = null, readyAt = null, at = now() } = {}) {
  const out = {
    view,
    tab,
    msSinceReady: typeof readyAt === 'number' ? Math.max(0, Math.round(at - readyAt)) : null,
    ...params,
  };
  if (out.tab == null) out.tab = tab;
  if (typeof out.query === 'string') out.query = landingQuery(out.query);
  return out;
}

/**
 * Send a landing event through the analytics beacon.
 *
 * @param {string} event - `sg_landing_*`
 * @param {object} [params] - the event's own params (with `destination` on exits)
 */
export function trackLandingEvent(event, params = {}) {
  const full = landingEventParams(params, { ...landing, at: now() });
  // The tab the next event happens on: a switch moves it, any other event
  // reports the one it saw.
  landing.tab = event === 'sg_landing_tab' && params.to ? params.to : full.tab;
  trackEvent(event, full);
}
